// =============================================================================
// ProviderApiAccessTab — provider-scoped API keys for the Provider Pull API.
// Platform admins generate/rotate/revoke the Bearer keys the provider uses to
// call /provider/orders (list + payload + claim). The plaintext key appears
// EXACTLY ONCE in the generation modal — only a hash is stored server-side.
// =============================================================================

import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input, Modal, ModalFooter, Spinner, useConfirm } from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProviderApiKey {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

interface GenerateResult {
  plaintextKey: string;
  apiKey: ProviderApiKey;
}

interface ProviderApiAccessTabProps {
  providerId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProviderApiAccessTab({ providerId }: ProviderApiAccessTabProps) {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [generated, setGenerated] = useState<GenerateResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const keysQuery = useQuery<{ data: ProviderApiKey[] }, Error>({
    queryKey: ['admin', 'provider', providerId, 'api-keys'],
    queryFn: () =>
      api.get<{ data: ProviderApiKey[] }>(
        `/api/v1/admin/providers/${providerId}/api-keys`,
      ),
  });

  const generateMutation = useMutation({
    mutationFn: (name: string) =>
      api.post<{ data: GenerateResult }>(
        `/api/v1/admin/providers/${providerId}/api-keys`,
        { name },
      ),
    onSuccess: (res) => {
      setGenerated(res.data);
      setCopied(false);
      setKeyName('');
      setFeedback(null);
      queryClient.invalidateQueries({
        queryKey: ['admin', 'provider', providerId, 'api-keys'],
      });
    },
    onError: (err: Error) =>
      setFeedback({ type: 'error', text: err.message || 'Failed to generate key.' }),
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) =>
      api.post<{ data: ProviderApiKey }>(
        `/api/v1/admin/providers/${providerId}/api-keys/${keyId}/revoke`,
        {},
      ),
    onSuccess: () => {
      setFeedback({ type: 'success', text: 'Key revoked.' });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'provider', providerId, 'api-keys'],
      });
    },
    onError: (err: Error) =>
      setFeedback({ type: 'error', text: err.message || 'Failed to revoke key.' }),
  });

  const handleGenerate = (e: FormEvent) => {
    e.preventDefault();
    if (keyName.trim().length < 3) {
      setFeedback({ type: 'error', text: 'Key name must be at least 3 characters.' });
      return;
    }
    generateMutation.mutate(keyName.trim());
  };

  const handleRevoke = async (key: ProviderApiKey) => {
    const ok = await confirm({
      title: 'Revoke API key',
      description: `"${key.name}" (${key.prefix}…) will stop working immediately. The provider must switch to another active key first. This cannot be undone.`,
      confirmLabel: 'Revoke',
      tone: 'danger',
    });
    if (ok) revokeMutation.mutate(key.id);
  };

  const handleCopy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.plaintextKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const closeGenerateModal = () => {
    // Once the plaintext has been shown, closing is final — it cannot be
    // retrieved again (only a hash is stored).
    setShowGenerateModal(false);
    setGenerated(null);
    setCopied(false);
  };

  const keys = keysQuery.data?.data ?? [];
  const activeKeys = keys.filter((k) => !k.revokedAt);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">Provider Pull API keys</h3>
          <p className="mt-1 text-sm text-text-secondary max-w-xl">
            Bearer keys for the provider&apos;s reconciliation client
            (<code className="text-xs">GET /provider/orders</code>, payload, claim).
            Rotate by generating a new key first; revoke the old one after the
            provider has switched over.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowGenerateModal(true)}>
          Generate key
        </Button>
      </div>

      {feedback && (
        <div
          className={`rounded-md px-4 py-3 text-sm ${
            feedback.type === 'success'
              ? 'bg-green-500/10 border border-green-500/20 text-green-500'
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}
        >
          {feedback.text}
        </div>
      )}

      {keysQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="md" label="Loading keys..." />
        </div>
      ) : keysQuery.isError ? (
        <p className="text-sm text-danger">{keysQuery.error.message}</p>
      ) : keys.length === 0 ? (
        <p className="text-sm text-text-tertiary py-8 text-center">
          No API keys yet. Generate one to enable the provider&apos;s pull access.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-tertiary">
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Prefix</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Last used</th>
                <th className="py-2 pr-4 font-medium">Created</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} className="border-b border-border/50">
                  <td className="py-3 pr-4 text-text-primary">{key.name}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-text-secondary">
                    tkp_{key.prefix}_…
                  </td>
                  <td className="py-3 pr-4">
                    {key.revokedAt ? (
                      <Badge variant="danger" size="sm">Revoked</Badge>
                    ) : (
                      <Badge variant="success" size="sm" dot>Active</Badge>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-text-secondary">{formatDate(key.lastUsedAt)}</td>
                  <td className="py-3 pr-4 text-text-secondary">{formatDate(key.createdAt)}</td>
                  <td className="py-3 text-right">
                    {!key.revokedAt && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleRevoke(key)}
                        disabled={revokeMutation.isPending}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeKeys.length > 1 && (
        <p className="text-xs text-amber-500">
          Multiple active keys exist — expected only during rotation. Revoke the
          old key once the provider has switched.
        </p>
      )}

      {/* Generate modal — shows the plaintext key exactly once */}
      <Modal
        open={showGenerateModal}
        onClose={closeGenerateModal}
        title={generated ? 'API key generated' : 'Generate API key'}
        size="md"
      >
        {generated ? (
          <div className="space-y-4">
            <div className="rounded-md bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-sm text-amber-600">
              Copy this key now — it is shown only once and cannot be retrieved
              later (only a hash is stored).
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-md bg-surface-secondary px-3 py-2 text-xs font-mono text-text-primary border border-border">
                {generated.plaintextKey}
              </code>
              <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <ModalFooter>
              <Button onClick={closeGenerateModal}>Done</Button>
            </ModalFooter>
          </div>
        ) : (
          <form onSubmit={handleGenerate} className="space-y-4">
            <Input
              label="Key name"
              placeholder='e.g. "Inkteo production poller"'
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              autoFocus
            />
            <ModalFooter>
              <Button variant="outline" type="button" onClick={closeGenerateModal}>
                Cancel
              </Button>
              <Button type="submit" loading={generateMutation.isPending}>
                Generate
              </Button>
            </ModalFooter>
          </form>
        )}
      </Modal>

      {dialog}
    </div>
  );
}
